import _isNil from 'lodash/isNil';
import IWorkflowTransition from './IWorkflowTransition';

/**
 * A state machine
 * 
 * @param {{}} properties: {
 * 
 *      - str init:         the initial state,
 * 
 * 
 *      - [{}] transitions: the possible state changes
 * 
 *          eg: { name: 'melt', from: 'solid', to: 'liquid' }
 *          - str name    : the method to call to initiate the transition, in this case - melt()
 *          - str from    : the state from which the transition can occur
 *          - str/func to : the state that the engine will be in once the transition has finished
 * 
 * 
 *      - {} methods:       additional hooks that will be called during the transition
 * 
 *          - default hooks to be overridden are 'onEnter' and 'onLeave'
 *          - on<TransitionName>, eg. 'onMelt'
 *          - onLeave<State> and onEnter<State>, eg. 'onLeaveSolid', 'onEnterLiquid'
 * 
 *  }
 * 
 *  eg: var wf = new WorkflowEngine({
        init: 'solid',
        transitions: [
            { name: 'melt', from: 'solid', to: 'liquid' },              <- multiple transitions listening for the same hook
            { name: 'melt', from: 'liquid', to: 'gas' },                <- (ignore the terrible chemistry)
            { name: 'freeze', from: 'liquid', to: 'solid' },            
            { name: 'vapourise', from: 'liquid', to: 'gas' },
            { name: 'condense', from: 'gas', to: 'liquid' },
            { 
                name: 'conditionalTransition', 
                from: 'liquid', 
                to: (param) => { // some logic here }                   <- can utilise a function to return the new state, 
            }                                                              nb: singular param only, use an object if multiple properties are needed
        ],
        methods: {            
            onEnter: function () { console.log('base entering method') },
            onLeave: function () { console.log('base exiting method') }
            onMelt: function () { console.log('I melted') },
            onFreeze: function () { console.log('I froze') },
            onVapourise: function () { console.log('I vapourised') },
            onCondense: function () { console.log('I condensed') },
            onLeaveSolid: function () { console.log('Leaving solid state') },
            onEnterGas: function () { console.log('Entering gaseous state') }
        }
    })
 */

class WorkflowEngine {
    state: any;
    transitions: any;
    methods: Array<any>;
    constructor(properties) {
        this.state = properties.initialState;
        this.transitions = {};
        this.methods = properties.methods;

        this._initialiseCoreTransitions(properties.transitions);
        this._initialiseOverrides();
    }

    _initialiseCoreTransitions(transitions: Array<IWorkflowTransition>) {
        for (let i = 0; i < transitions.length; i++) {
            // initialise objects
            const transition = transitions[i];
            if (_isNil(this.transitions[transition.action])) {
                this.transitions[transition.action] = {};
            }

            this.transitions[transition.action][transition.currentState] = {
                transition: (args) => {
                    this.transition(
                        transition.action,
                        transition.currentState,
                        transition.newState,
                        args
                    );
                },
            };

            // initialise handlers
            this[transition.action] = (input) => {
                for (const key in this.transitions) {
                    if (key == transition.action) {
                        for (const property in this.transitions[key]) {
                            if (property == this.state) {
                                this.transitions[key][property].transition(
                                    input
                                );
                                return;
                            }
                        }
                    }
                }
            };
        }
    }

    _initialiseOverrides() {
        for (const methodName in this.methods) {
            if (methodName === 'onEnter') {
                this._onEnter = this.methods[methodName];
            }

            if (methodName === 'onLeave') {
                this._onLeave = this.methods[methodName];
            }
        }
    }

    _onLeave() {
        // to be overridden
    }

    _onEnter() {
        // to be overridden
    }

    _baseTransitionHandler(methodPrefix, state) {
        for (const methodName in this.methods) {
            const methodStateArray = methodName.split(methodPrefix);
            if (methodStateArray.length > 1) {
                const name = methodStateArray[1].toLowerCase();
                if (typeof state === 'string' && name === state.toLowerCase()) {
                    this.methods[methodName]();
                }
            }
        }
    }

    _handleTransition(newState) {
        this._baseTransitionHandler('on', newState);
    }

    _handleLeave(initialState) {
        this._baseTransitionHandler('onLeave', initialState);
    }

    _handleEnter(newState) {
        this._baseTransitionHandler('onEnter', newState);
    }

    transition(transitionName, currentState, newState, args) {
        this._onLeave();
        this._handleLeave(currentState);
        this._handleTransition(transitionName);
        this.state = typeof newState === 'string' ? newState : newState(args);
        this._onEnter();
        this._handleEnter(newState);
    }
}

export default WorkflowEngine;
